import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import me from "models/me";
import CircularProgress from "material-ui/CircularProgress";
import Loading from "components/loading";
import { Form } from "formsy-react";
import Checkbox from "material-ui/Checkbox";
import Input from "components/form/text-field";
import Button from "components/form/button";
import ChevronIcon from "material-ui/svg-icons/navigation/chevron-right";
import { merge, $, media, presets } from "glamor";
import theme from "styles/theme";
import { DATATEST } from "constants/integration";
import { Map } from "immutable";
import * as validators from "lib/validators";
import { replaceSingleInternalURL } from "helpers/url";
import { FormatMessage } from "react-globalize";
import { NotACustomer } from "./not-a-customer";
import { checkAuthenticationFlow, getLsidConfig, lsOneLoginRedirect } from "lib/lsid";
import { FlowTypes } from "lib/lsid/enums";
import { FormTitleLogo } from "./form-title-logo";
import { buildAndSubmitHiddenForm } from "./utils";

interface ILoginFormProps {
    model?: any;
    siteLogo?: string;
    siteRegURL?: string;
    redirect?: string;
    error?: string;
    onRequestForgotPassword: ({ email, recaptchaClientKey }) => void;
    onResetState: () => void;
    authentication: any;
    onForgotPassword?: () => void;
    onRedirectToForgotPassword?: () => void;
    onSetDisplayHelpFooter?: (display: boolean) => void;
    onTrackLoginArticles: () => void;
    onTrackLoginRegistration: () => void;
    onTrackLoginVideo: () => void;
    onSwitchLoginFlow: (flow: FlowTypes) => void;
    setSharedEmail: (email?: string) => void;
    sharedEmail?: string;
}

interface ILoginFormState {
    loading: boolean;
    error?: string;
    remember: boolean;
    email?: string;
    password?: string;
    errors: any;
    failedTries: number;
    notACustomerOpen: boolean;
}

export const LoginFormWrapper = (props): JSX.Element => {
    const styles = merge(
        {
            zIndex: 3,
            margin: "0 auto",
            width: "84%",
            padding: "0px",
            backgroundColor: "transparent",
        },
        $(" .loading", {
            position: "fixed",
            top: "0px",
            left: "0px",
            width: "100%",
            height: "100%",
            zIndex: 200,
            backgroundColor: "rgba(255,255,255, .2)",
        }),
        $(" .loading > div", {
            position: "absolute !important",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 201,
        }),
        $(".branded", {
            top: "12px",
        }),
        $(" .option-link", {
            display: "block",
            marginTop: "12px",
            fontSize: "14px",
            textAlign: "center",
        }),
        $(" .option-link a", {
            color: theme.palette.darkerGrey,
            cursor: "pointer",
        }),
        $(" .option-link a:hover", {
            color: theme.palette.darkestGrey,
        }),
        $(".branded .option-link a", {
            color: "#f1f1f1",
            textShadow: "0 0 2px rgba(0,0,0, .2)",
        }),
        $(".branded .option-link a:hover", {
            color: "#fff",
        }),
        $(" h1", {
            textAlign: "center",
            margin: "16px 0 16px",
        }),
        $(".branded  h1", {
            margin: "16px 0 0px",
        }),
        $(" h1 img", {
            maxWidth: "100%",
        }),
        $(" .loginSubmit", {
            width: "100%",
        }),
        $(" .loginSubmit span", {
            padding: "0 8px !important",
        }),
        $(" .actions", {
            display: "flex",
            paddingTop: "20px",
        }),
        $(" .actionButton", {
            flexGrow: 1,
        }),
        $(" .forgotPasswordInfo", {
            display: "block",
            margin: "16px 0",
            fontSize: "16px",
            lineHeight: "21px",
        }),
        $(" .forgotPasswordForm", {
            paddingBottom: "26px",
        }),
        $(" .recaptchaContainer", {
            height: "78px",
            width: "100%",
        }),
        $(" .forgotPasswordContainer", {
            padding: "19px",
        }),
        $(" .emailFooter", {
            padding: "19px",
        }),
        $(" .bottomLinks", {
            textAlign: "center",
            paddingTop: "46px",
            fontSize: "14px",
        }),
        media(
            presets.phablet,
            merge(
                {
                    top: "50%",
                    width: "340px",
                },
                $(" .formset", {
                    marginTop: "8px",
                    paddingBottom: "16px",
                })
            )
        )
    );

    return <div {...{ ...props, ...styles }}>{props.children}</div>;
};

export class LoginForm extends React.Component<ILoginFormProps, ILoginFormState> {
    loading = false;
    _form: any;

    muiStyles = {
        nuOrderFull: {
            width: "unset",
            maxWidth: "100%",
            height: "unset",
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: props.error,
            remember: true,
            errors: Map(),
            failedTries: 0,
            notACustomerOpen: false,
            email: this.props.sharedEmail,
        };
    }

    handleRemember = (e, remember) => {
        this.setState({ remember });
    };

    handleForgotPassword = (e) => {
        if (this.props.onForgotPassword) {
            e.preventDefault();
            e.stopPropagation();
            this.props.onForgotPassword(); // custom processing of forgot password instead of opening dialog
        }
    };

    validateForm = () => {
        let valid = true;
        const { email, password } = this.state;
        let newErrors = this.state.errors;
        if (!email) {
            newErrors = newErrors.set("email", formatMessage("This field is required."));
            valid = false;
        } else if (!validators.default.email(email)) {
            newErrors = newErrors.set("email", formatMessage("Enter a valid email address."));
            valid = false;
        }

        if (!password) {
            newErrors = newErrors.set("password", formatMessage("This field is required."));
            valid = false;
        }

        this.setState({ errors: newErrors });

        return valid;
    };

    handleSubmitButtonTap = () => {
        if (this.validateForm()) {
            this.handleSubmit();
        }
    };

    handleEmailChange = (event: any, value: string) => {
        this.setState({ email: value });
        if (!value) {
            this.setState({ errors: this.state.errors.set("email", formatMessage("This field is required.")) });
        } else {
            this.setState({ errors: this.state.errors.delete("email") });
        }
    };

    handlePasswordChange = (event: any, value: string) => {
        this.setState({ password: value });

        if (!value) {
            this.setState({ errors: this.state.errors.set("password", formatMessage("This field is required.")) });
        } else {
            this.setState({ errors: this.state.errors.delete("password") });
        }
    };

    handleSubmit = async () => {
        const { remember, email, password } = this.state;
        let { redirect } = this.props;

        if (!redirect && this.props.model) {
            redirect = `/brand/${this.props.model._id}`;
        }
        if (lsOneLoginRedirect(email, redirect)) {
            return null;
        }

        if (!this.loading) {
            try {
                const credentials = { email, password, redirect_after_login: redirect, rememberMe: remember };
                await checkAuthenticationFlow(credentials);
                this.setState({ loading: true });
                this.loading = true;
                buildAndSubmitHiddenForm(credentials);
            } catch (e) {
                this.setState({ loading: true });
                this.loading = true;
                me.login(
                    {
                        email: email.trim(),
                        password: password.trim(),
                        remember,
                        redirect,
                    },
                    (err) => {
                        if (err) {
                            if (err.message) {
                                this.setState({ error: err.message });
                            }
                            this.loading = false;
                            const failedTries = (this.state.failedTries + 1) % 3;
                            this.setState({ loading: false, failedTries });
                            failedTries === 0 && this.props.onRedirectToForgotPassword();
                        } else {
                            if (this.props.model) {
                                // hack to redirect to custom login brand
                                me.last_brand = this.props.model._id;
                            }
                        }
                    }
                );
            }
        }
    };

    handleOpenNotACustomerScreen = () => {
        this.props.onTrackLoginRegistration();
        this.setState({ notACustomerOpen: true });
    };

    handleCloseNotACustomerScreen = () => {
        this.setState({ notACustomerOpen: false });
    };

    handleSSOLoginFlow = () => {
        this.props.setSharedEmail(this.state.email);
        this.props.onSwitchLoginFlow(FlowTypes.SSO);
    };

    render() {
        const { model, redirect, siteLogo, siteRegURL } = this.props;
        const { error, remember, errors, notACustomerOpen, email } = this.state;
        const { hasSSO } = getLsidConfig();
        const { lsid_observer } = me.ldClient.allFlags();

        if (me.isSignedIn() && !redirect) {
            return <Loading />;
        }

        if (notACustomerOpen) {
            return <NotACustomer onCloseNotACustomer={this.handleCloseNotACustomerScreen} />;
        }

        return (
            <LoginFormWrapper data-test={DATATEST.LOGIN.LOGIN_FORM} className={model ? "branded" : undefined}>
                <FormTitleLogo siteLogo={siteLogo} model={model} />
                {this.state.loading && (
                    <div className="loading">
                        {/* check styling with loadingProgress */}
                        <CircularProgress />
                    </div>
                )}

                <Form ref={(ref) => (this._form = ref)}>
                    {error && (
                        <div data-test={DATATEST.LOGIN.ERROR} className="alert alert-error" role="alert">
                            {error}
                        </div>
                    )}
                    <fieldset className="formset">
                        <Input
                            name="email"
                            value={email ?? ""}
                            disabled={this.state.loading}
                            placeholder={formatMessage("Email")}
                            errorText={errors.get("email") ? errors.get("email") : null}
                            inputStyle={{ background: "white" }}
                            style={{ margin: "24px 0 0" }}
                            allowWhitespace={false}
                            large={true}
                            fullWidth={true}
                            dataTest={DATATEST.LOGIN.EMAIL}
                            onChange={this.handleEmailChange}
                        />
                        <Input
                            name="password"
                            type="password"
                            disabled={this.state.loading}
                            placeholder={formatMessage("Password")}
                            errorText={errors.get("password") ? errors.get("password") : null}
                            inputStyle={{ background: "white" }}
                            style={{ margin: "24px 0 0" }}
                            allowWhitespace={false}
                            large={true}
                            fullWidth={true}
                            require={"required"}
                            onChange={this.handlePasswordChange}
                            dataTest={DATATEST.LOGIN.PASSWORD}
                        />
                        {!lsid_observer && (
                            <Checkbox
                                data-test={DATATEST.LOGIN.REMEMBER_ME}
                                name="remember"
                                id="id_remember"
                                disabled={this.state.loading}
                                onCheck={this.handleRemember}
                                checked={remember}
                                label={formatMessage("Remember me")}
                                labelStyle={
                                    model
                                        ? {
                                              color: "white",
                                              textShadow: "0 0 2px rgba(0,0,0, .2)",
                                          }
                                        : {}
                                }
                                iconStyle={model ? { fill: "white" } : {}}
                                style={{ margin: "16px 0 12px" }}
                            />
                        )}
                    </fieldset>
                    <Button
                        type="submit"
                        onClick={this.handleSubmitButtonTap}
                        disabled={this.state.loading}
                        data-test={DATATEST.LOGIN.SUBMIT}
                        label={formatMessage("Log In")}
                        labelPosition="before"
                        icon={<ChevronIcon color="white" />}
                        className="loginSubmit"
                        primary={true}
                        large={true}
                    />
                    {hasSSO && (
                        <div className="option-link">
                            <a data-test={DATATEST.LOGIN.SWITCH_SSO} onClick={this.handleSSOLoginFlow}>
                                {formatMessage("Log In with Single Sign On")}
                            </a>
                        </div>
                    )}
                    <div className="option-link">
                        <a
                            href={`/forgot-password?email=${this.state.email ?? ""}&siteLogo=${siteLogo || ""}&model=${
                                model ? encodeURIComponent(JSON.stringify(model)) : ""
                            }`}
                            data-test={DATATEST.LOGIN.FORGOT_PASSWORD_LINK}
                            onClick={this.handleForgotPassword}
                        >
                            {formatMessage("Forgot your password?")}
                        </a>
                    </div>
                    {siteRegURL ? (
                        <div className="option-link">
                            <a
                                href={replaceSingleInternalURL(siteRegURL)}
                                target="_blank"
                                data-test="site-registration-link"
                            >
                                {formatMessage("Not Registered?")}
                            </a>
                        </div>
                    ) : (
                        <div className="bottomLinks">
                            <FormatMessage
                                path="Not a customer? [link]Sign up[link]"
                                elements={{
                                    link: (
                                        <a
                                            href="#"
                                            data-test={DATATEST.LOGIN.NOT_A_CUSTOMER}
                                            onClick={this.handleOpenNotACustomerScreen}
                                        />
                                    ),
                                }}
                            />
                        </div>
                    )}
                </Form>
            </LoginFormWrapper>
        );
    }
}

export default LoginForm;
